import { SheetModeControls } from "@/components/r3f/controls/sheet-mode-controls";
import { TomographicOverlayViewPipeline } from "@/components/r3f/effects/tomographic-view-pipeline";
import { CadRenderer } from "@/components/r3f/renderers/cad-renderer";
import { SheetRenderer } from "@/components/r3f/renderers/sheet-renderer";
import { useSceneBackgroundColor } from "@/components/r3f/utils/scene-background";
import {
  centerOrthoCamera,
  useCenterCameraOnPlaceholders,
  useOrthoCameraRotationFromSheet,
} from "@/hooks/use-center-camera-on-placeholders";
import { useObjectVisibility } from "@/hooks/use-object-visibility";
import { useCached3DObject } from "@/object-cache";
import { isFromSheetObject } from "@/object-cache-type-guard";
import { AlignmentViewLayout } from "@/store/modes/alignment-ui-types";
import {
  selectAlignmentAnchorPositions,
  selectCrossSectionEnabledForTomographicView,
  selectIncrementalSheetCadTransform,
  selectSheetCadAlignmentLayout,
  selectSheetCadAlignmentReference,
  selectSheetElevation,
} from "@/store/modes/sheet-cad-alignment-mode-selectors";
import {
  setMovingElementAnchor1,
  setMovingElementAnchor2,
  setReferenceElementAnchor1,
  setReferenceElementAnchor2,
  setSheetCadIncrementalTransform,
  SheetCadAlignReference,
} from "@/store/modes/sheet-cad-alignment-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  AnchorPairPlacement,
  ClearPass,
  CopyToScreenPass,
  DesaturatePass,
  EffectPipeline,
  FilteredRenderPass,
  Map2DControls,
  quaternionToVector4Tuple,
  selectIElementWorldPosition,
  TomographicModelPass,
  TwoPointAlignment,
  UniformLights,
  useNonExhaustiveEffect,
  useReproportionCamera,
  View,
} from "@faro-lotv/app-component-toolbox";
import {
  IElementGenericImgSheet,
  IElementImg360,
  IElementModel3dStream,
} from "@faro-lotv/ielement-types";
import { useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { Box3, Quaternion, Vector3, Vector3Tuple } from "three";
import {
  useCadTomographicMaterial,
  useCameraInCurrentScene,
  useCenterCameraOnBoundingBoxIn2D,
  useCenterCameraOnCadModel,
  useClippingPlanesAtModelElevation,
  useNewOrthographicCamera,
  useSheetBoundingBox,
} from "../alignment-modes-commons/align-to-cad-utils";
import {
  AlignmentTransform,
  alignmentTransformToMatrix4,
} from "../alignment-modes-commons/alignment-transform";
import { computeSplitScreenAlignment } from "../alignment-modes-commons/compute-split-screen-alignment";
import { useOverlayElements } from "../overlay-elements-context";
import { RenderDispatch } from "../overview-mode/render-dispatch-utils";

export type AlignSheetCadSceneProps = {
  /** The active sheet for alignment */
  activeSheet: IElementGenericImgSheet;

  /** The active cad model for alignment */
  activeCad: IElementModel3dStream;

  /** The bounding box of the cad model in world */
  cadBox: Box3;
};

type AlignSheetCadSplitScreenProps = {
  /** The active sheet for alignment */
  activeSheet: IElementGenericImgSheet;

  /** The active cad model for alignment */
  activeCad: IElementModel3dStream;

  /** The bounding box of the cad model in world */
  cadBox: Box3;

  /** First screen in split screen (left or top screen) */
  firstScreen: HTMLDivElement;

  /** Second  screen in split screen (right or bottom screen) */
  secondScreen: HTMLDivElement;
};

/** @returns the scene for align the cad to sheet in 2d */
export function AlignSheetCadScene({
  activeSheet,
  activeCad,
  cadBox,
}: AlignSheetCadSceneProps): JSX.Element {
  const alignmentLayout = useAppSelector(selectSheetCadAlignmentLayout);
  const { firstScreen, secondScreen } = useOverlayElements();

  return alignmentLayout === AlignmentViewLayout.splitScreen &&
    firstScreen &&
    secondScreen ? (
    <AlignSheetCadSplitScreen
      {...{ activeSheet, activeCad, cadBox, firstScreen, secondScreen }}
    />
  ) : (
    <AlignSheetCadOverlayScene {...{ activeSheet, activeCad, cadBox }} />
  );
}

/** @returns the split-screen scene for align the sheet and cad in 2d */
function AlignSheetCadSplitScreen({
  activeSheet,
  activeCad,
  cadBox,
  firstScreen,
  secondScreen,
}: AlignSheetCadSplitScreenProps): JSX.Element {
  const background = useSceneBackgroundColor();
  const sheetCamera = useNewOrthographicCamera();
  const modelCamera = useNewOrthographicCamera();

  const sheetElevation = useAppSelector(selectSheetElevation);
  const alignmentReference = useAppSelector(selectSheetCadAlignmentReference);

  const cadModel = useCached3DObject(activeCad, "AlignSheetCadSplitScreen");
  useObjectVisibility(cadModel, true);

  const cadCenteringData = useCenterCameraOnCadModel(
    cadModel,
    cadBox,
    secondScreen,
  );
  const sheetElements = useMemo(() => [activeSheet], [activeSheet]);
  const cameraRotation = useOrthoCameraRotationFromSheet(activeSheet.id);
  const sheetCenteringData = useCenterCameraOnPlaceholders({
    sheetElements,
    cameraRotation,
    placeholders: new Array<IElementImg360>(),
    viewAspectRatio: firstScreen.clientWidth / firstScreen.clientHeight,
  });

  useNonExhaustiveEffect(() => {
    centerOrthoCamera(sheetCamera, sheetCenteringData);
    centerOrthoCamera(modelCamera, cadCenteringData);
  }, []);

  const sheet = useCached3DObject(activeSheet, "AlignSheetCadSplitScreen");
  const sheetPosition = useAppSelector(
    selectIElementWorldPosition(activeSheet.id),
  );

  useCadTomographicMaterial(cadModel);
  const isCrossSectionEnabled = useAppSelector(
    selectCrossSectionEnabledForTomographicView,
  );
  const clippingPlanes = useClippingPlanesAtModelElevation(
    cadModel,
    sheetElevation,
    isCrossSectionEnabled,
  );

  const [controlsEnabled, setControlsEnabled] = useState(true);

  const dispatch = useAppDispatch();
  const alignmentAnchorPositions = useAppSelector(
    selectAlignmentAnchorPositions,
  );

  // compute split screen alignment when there is change in anchor positions
  useEffect(() => {
    const alignmentTransform = computeSplitScreenAlignment(
      alignmentAnchorPositions,
      alignmentReference === SheetCadAlignReference.bimModel,
    );
    if (alignmentTransform) {
      dispatch(setSheetCadIncrementalTransform(alignmentTransform));
    }
  }, [alignmentAnchorPositions, alignmentReference, dispatch]);

  const changeReferenceElementAnchor1Position = useCallback(
    (position: Vector3Tuple) => {
      dispatch(setReferenceElementAnchor1(position));
    },
    [dispatch],
  );
  const changeReferenceElementAnchor2Position = useCallback(
    (position: Vector3Tuple) => {
      dispatch(setReferenceElementAnchor2(position));
    },
    [dispatch],
  );
  const changeMovingElementAnchor1Position = useCallback(
    (position: Vector3Tuple) => {
      dispatch(setMovingElementAnchor1(position));
    },
    [dispatch],
  );
  const changeMovingElementAnchor2Position = useCallback(
    (position: Vector3Tuple) => {
      dispatch(setMovingElementAnchor2(position));
    },
    [dispatch],
  );

  return (
    <>
      <View
        camera={sheetCamera}
        trackingElement={
          alignmentReference === SheetCadAlignReference.bimModel
            ? firstScreen
            : secondScreen
        }
        background={background}
        hasSeparateScene
      >
        <AnchorPairPlacement
          onPointerDown={() => setControlsEnabled(false)}
          onPointerUp={() => setControlsEnabled(true)}
          anchor1Position={
            alignmentReference === SheetCadAlignReference.bimModel
              ? alignmentAnchorPositions.movingElementAnchor1
              : alignmentAnchorPositions.referenceElementAnchor1
          }
          anchor2Position={
            alignmentReference === SheetCadAlignReference.bimModel
              ? alignmentAnchorPositions.movingElementAnchor2
              : alignmentAnchorPositions.referenceElementAnchor2
          }
          changeAnchor1Position={
            alignmentReference === SheetCadAlignReference.bimModel
              ? changeMovingElementAnchor1Position
              : changeReferenceElementAnchor1Position
          }
          changeAnchor2Position={
            alignmentReference === SheetCadAlignReference.bimModel
              ? changeMovingElementAnchor2Position
              : changeReferenceElementAnchor2Position
          }
        >
          <SheetRenderer sheet={sheet} />
        </AnchorPairPlacement>
        <EffectPipeline>
          <FilteredRenderPass filter={(o) => isFromSheetObject(o)} />
          <DesaturatePass />
          <FilteredRenderPass
            filter={(o) => !isFromSheetObject(o)}
            clear={false}
            clearDepth={false}
          />
          <CopyToScreenPass />
        </EffectPipeline>
        <SheetModeControls
          camera={sheetCamera}
          referencePlaneHeight={sheetPosition[1]}
          enabled={controlsEnabled}
        />
      </View>
      <View
        camera={modelCamera}
        trackingElement={
          alignmentReference === SheetCadAlignReference.sheet
            ? firstScreen
            : secondScreen
        }
        background={background}
        hasSeparateScene
      >
        <UniformLights ambientIntensity={1.0} />
        <AnchorPairPlacement
          onPointerDown={() => setControlsEnabled(false)}
          onPointerUp={() => setControlsEnabled(true)}
          anchor1Position={
            alignmentReference === SheetCadAlignReference.sheet
              ? alignmentAnchorPositions.movingElementAnchor1
              : alignmentAnchorPositions.referenceElementAnchor1
          }
          anchor2Position={
            alignmentReference === SheetCadAlignReference.sheet
              ? alignmentAnchorPositions.movingElementAnchor2
              : alignmentAnchorPositions.referenceElementAnchor2
          }
          changeAnchor1Position={
            alignmentReference === SheetCadAlignReference.sheet
              ? changeMovingElementAnchor1Position
              : changeReferenceElementAnchor1Position
          }
          changeAnchor2Position={
            alignmentReference === SheetCadAlignReference.sheet
              ? changeMovingElementAnchor2Position
              : changeReferenceElementAnchor2Position
          }
        >
          <CadRenderer
            cadModel={cadModel}
            clippingPlanes={clippingPlanes}
            clippingInLocalTransform={clippingPlanes.length > 0}
          />
        </AnchorPairPlacement>
        <EffectPipeline>
          <ClearPass clearColor={background} />
          <TomographicModelPass camera={modelCamera} bias={0.05} />
          <FilteredRenderPass
            filter={(o) => o.userData.type !== RenderDispatch.CadModel}
            clear={false}
            clearDepth={false}
          />
          <CopyToScreenPass />
        </EffectPipeline>
        <SheetModeControls
          camera={modelCamera}
          referencePlaneHeight={sheetElevation}
          enabled={controlsEnabled}
        />
      </View>
    </>
  );
}

type AlignSheetCadOverlaySceneProps = {
  /** The active sheet for alignment */
  activeSheet: IElementGenericImgSheet;

  /** The active cad model for alignment */
  activeCad: IElementModel3dStream;

  /** The bounding box of the cad model in world */
  cadBox: Box3;
};

/** @returns the overlay scene for align the sheet and cad in 2d overlay*/
function AlignSheetCadOverlayScene({
  activeSheet,
  activeCad,
  cadBox,
}: AlignSheetCadOverlaySceneProps): JSX.Element {
  const sheet = useCached3DObject(activeSheet, "AlignSheetCadOverlayScene");
  const sheetPosition = useAppSelector(
    selectIElementWorldPosition(activeSheet.id),
  );
  const alignmentReference = useAppSelector(selectSheetCadAlignmentReference);

  const [controlsEnabled, setControlsEnabled] = useState(true);
  const cadModel = useCached3DObject(activeCad, "AlignSheetCadOverlayScene");
  const dispatch = useAppDispatch();

  const { singleScreen } = useOverlayElements();
  const background = useSceneBackgroundColor();

  const incrementalTransform = useAppSelector(
    selectIncrementalSheetCadTransform,
  );

  useObjectVisibility(cadModel, true);

  // TwoPointAlignment apply this transform to the CAD model, so it
  // can be aligned to the sheet in the view
  const [transform] = useState<AlignmentTransform>(
    () =>
      incrementalTransform ?? {
        position: [0, 0, 0],
        quaternion: [0, 0, 0, 1],
        scale: [1, 1, 1],
      },
  );

  const camera = useNewOrthographicCamera();
  useCameraInCurrentScene(camera);

  const sheetElements = useMemo(() => [sheet.iElement], [sheet.iElement]);
  const sheetBox = useSheetBoundingBox(sheetElements);
  const movingBox =
    alignmentReference === SheetCadAlignReference.bimModel ? sheetBox : cadBox;
  const referenceBox =
    alignmentReference === SheetCadAlignReference.bimModel ? cadBox : sheetBox;
  const boundingBox = useMemo(
    () =>
      movingBox
        .clone()
        .applyMatrix4(alignmentTransformToMatrix4(transform))
        .union(referenceBox),
    [movingBox, referenceBox, transform],
  );

  const size = useThree((s) => s.size);
  const centeringData = useCenterCameraOnBoundingBoxIn2D(
    boundingBox,
    size.width / size.height,
  );

  const [initialCenteringData] = useState(() => centeringData);
  useEffect(() => {
    centerOrthoCamera(camera, initialCenteringData);
  }, [camera, initialCenteringData]);

  useReproportionCamera(camera);
  useCadTomographicMaterial(cadModel);

  const sheetElevation = useAppSelector(selectSheetElevation);
  const isCrossSectionEnabled = useAppSelector(
    selectCrossSectionEnabledForTomographicView,
  );
  const clippingPlanes = useClippingPlanesAtModelElevation(
    cadModel,
    sheetElevation,
    isCrossSectionEnabled,
  );

  const onTransformChanged = useCallback(
    (position: Vector3, quaternion: Quaternion, scale: Vector3) => {
      const transform: AlignmentTransform = {
        scale: scale.toArray(),
        position: position.toArray(),
        quaternion: quaternionToVector4Tuple(quaternion),
      };

      dispatch(setSheetCadIncrementalTransform(transform));
    },
    [dispatch],
  );

  return (
    <View
      camera={camera}
      trackingElement={singleScreen}
      background={background}
      hasSeparateScene
    >
      <UniformLights />
      {alignmentReference === SheetCadAlignReference.bimModel && (
        <CadRenderer
          cadModel={cadModel}
          clippingPlanes={clippingPlanes}
          clippingInLocalTransform={clippingPlanes.length > 0}
        />
      )}
      {alignmentReference === SheetCadAlignReference.sheet && (
        <SheetRenderer sheet={sheet} />
      )}
      <TwoPointAlignment
        onPointerDown={() => setControlsEnabled(false)}
        onPointerUp={() => setControlsEnabled(true)}
        onTransformChanged={onTransformChanged}
        isScaleEnabled={alignmentReference === SheetCadAlignReference.bimModel}
        {...transform}
      >
        {alignmentReference === SheetCadAlignReference.bimModel && (
          <SheetRenderer sheet={sheet} />
        )}
        {alignmentReference === SheetCadAlignReference.sheet && (
          <CadRenderer
            cadModel={cadModel}
            clippingPlanes={clippingPlanes}
            clippingInLocalTransform={clippingPlanes.length > 0}
          />
        )}
      </TwoPointAlignment>
      <TomographicOverlayViewPipeline
        sheet={activeSheet}
        camera={camera}
        bias={0}
        filter={(o) => o.userData.type === RenderDispatch.CadModel}
      />
      <Map2DControls
        camera={camera}
        referencePlaneHeight={sheetPosition[1]}
        enabled={controlsEnabled}
        isPrimaryControl={false}
      />
    </View>
  );
}
